import keyBy from 'lodash/keyBy';
import uniq from 'lodash/uniq';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { fetchUserCollections } from '../../core/users/users.utils';
import { throttleDispatchFetch } from '../../utils';
import { useStoreCollectionSync } from '../../utils/useVersionSync';
import { throttledFetchWorkshopSessions } from '../../workshop-session/store/workshopSessions.actions';
import { throttledFetchWorkshops } from '../../workshops/store/workshops.actions';
import { ProgramEntity } from '../types';

const throttledFetchUsers = throttleDispatchFetch(fetchUserCollections);

type DataStore = { dataById?: Record<string, ProgramEntity> };

type StateType = {
  data: { collections?: Record<string, DataStore> };
  users: { usersById?: Record<string, ProgramEntity> };
  workshops: { workshopsById?: Record<string, ProgramEntity> };
  workshopSessions: { workshopSessions?: ProgramEntity[] };
};

/**
 * Alimente et tient à jour le store dans lequel le programme d'un groupe est
 * résolu : workshops et sessions (synchronisés), et les collections de users
 * quand la configuration en désigne (les data sont chargées au démarrage).
 */
export function useProgramStoreSync(userCollections: string[] = []): void {
  useStoreCollectionSync({ resourceType: 'workshops', fetcher: throttledFetchWorkshops });
  useStoreCollectionSync({
    resourceType: 'workshopSessions',
    fetcher: throttledFetchWorkshopSessions,
  });

  const collections = useMemo(() => uniq(userCollections.filter((v) => v)), [userCollections]);
  useEffect(() => {
    if (collections.length) throttledFetchUsers(collections);
  }, [collections]);
}

/**
 * Entités du store indexées par id : collections de data, contributeurs,
 * workshops et sessions. Le programme d'un groupe ne stocke que des ids, et
 * ils peuvent pointer vers n'importe laquelle de ces collections selon la
 * configuration du formulaire : on les résout donc sans config supplémentaire.
 */
export function useProgramEntitiesById(): Record<string, ProgramEntity> {
  const dataCollections = useSelector((state: StateType) => state.data?.collections);
  const usersById = useSelector((state: StateType) => state.users?.usersById);
  const workshopsById = useSelector((state: StateType) => state.workshops?.workshopsById);
  const workshopSessions = useSelector(
    (state: StateType) => state.workshopSessions?.workshopSessions,
  );

  return useMemo(
    () => ({
      ...workshopsById,
      ...keyBy(workshopSessions || [], '_id'),
      ...usersById,
      // Les collections de data en dernier : ce sont elles qui portent
      // habituellement les démos et les conférences
      ...Object.values(dataCollections || {}).reduce(
        (acc, dataStore) => Object.assign(acc, dataStore?.dataById),
        {} as Record<string, ProgramEntity>,
      ),
    }),
    [dataCollections, usersById, workshopsById, workshopSessions],
  );
}
